// ════════════════════════════════════════════════════════════════════════
// AppSidebarMenu.tsx — Contenido del sidebar del panel, compartido
// ════════════════════════════════════════════════════════════════════════
// Extraído a su propio componente para que agregar una sección nueva al
// panel sea solo agregar una línea (o un MenuGroup) aquí.
// ════════════════════════════════════════════════════════════════════════

import { DashboardLayout } from '../../layouts/DashboardLayout/DashboardLayout';
import {
  BriefcaseIcon,
  BarChartIcon,
  GlobeIcon,
  StarIcon,
  WrenchIcon,
  FlaskIcon,
  ShieldIcon,
} from '@/shared/ui/Icons/Icons';
import { useAuth } from '@/shared/context/AuthContext';
import { CV_RESOURCES, BACKEND_RESOURCES, PSICOLOGIA_RESOURCES, OFERTAS_RESOURCES, CERTIFICACIONES_RESOURCES } from '@/data/resources';
import { DIALOGUES } from '@/data/dialogues';
import { MONOLOGUES } from '@/data/monologues';
import { QA_SCRIPTS } from '@/data/qaScripts';

// URL del Grafana del clúster de Kubernetes (dashboards de monitoreo de la
// API) — dominio propio con Ingress+TLS, login separado del de este panel.
// Ver backend/reinvent-x-app-k8s/k8s/07-grafana-ingress.yaml.
const GRAFANA_URL: string = import.meta.env.VITE_GRAFANA_URL ?? '';

export function AppSidebarMenu() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  return (
    <>
      <DashboardLayout.MenuItem to="/dashboard/leads" icon={<BriefcaseIcon />}>
        Leads
      </DashboardLayout.MenuItem>

      {/* CVs — las distintas versiones del CV en HTML, ver src/data/resources.ts */}
      <DashboardLayout.MenuGroup label="CVs" icon={<StarIcon />}>
        {CV_RESOURCES.map((r) => (
          <DashboardLayout.MenuItem key={r.slug} to={`/dashboard/recursos/${r.slug}`}>
            {r.title.replace(/^CV — /, '')}
          </DashboardLayout.MenuItem>
        ))}
      </DashboardLayout.MenuGroup>

      {/* Guías Backend — PDFs y HTML de estudio técnico */}
      <DashboardLayout.MenuGroup label="Guías Backend" icon={<WrenchIcon />}>
        {BACKEND_RESOURCES.map((r) => (
          <DashboardLayout.MenuItem key={r.slug} to={`/dashboard/recursos/${r.slug}`}>
            {r.title}
          </DashboardLayout.MenuItem>
        ))}
      </DashboardLayout.MenuGroup>

      {/* Certificaciones — guías de estudio para exámenes (HTML en
          public/resources/certificaciones), se abren en el visor del panel. */}
      <DashboardLayout.MenuGroup label="Certificaciones" icon={<ShieldIcon />}>
        {CERTIFICACIONES_RESOURCES.map((r) => (
          <DashboardLayout.MenuItem key={r.slug} to={`/dashboard/recursos/${r.slug}`}>
            {r.title}
          </DashboardLayout.MenuItem>
        ))}
      </DashboardLayout.MenuGroup>

      {/* Inglés / Diálogos — guion bilingüe original + los nuevos guiones
          de práctica (diálogos, discurso y Q&A) traídos de la carpeta CV. */}
      <DashboardLayout.MenuGroup label="Inglés / Diálogos" icon={<GlobeIcon />}>
        <DashboardLayout.MenuItem to="/dashboard/entrevista">
          Entrevista: Tech Lead .NET + React
        </DashboardLayout.MenuItem>
        {DIALOGUES.map((d) => (
          <DashboardLayout.MenuItem key={d.slug} to={`/dashboard/practica/${d.slug}`}>
            {d.title}
          </DashboardLayout.MenuItem>
        ))}
        {MONOLOGUES.map((m) => (
          <DashboardLayout.MenuItem key={m.slug} to={`/dashboard/pitch/${m.slug}`}>
            {m.title}
          </DashboardLayout.MenuItem>
        ))}
        {QA_SCRIPTS.map((q) => (
          <DashboardLayout.MenuItem key={q.slug} to={`/dashboard/guion/${q.slug}`}>
            {q.title}
          </DashboardLayout.MenuItem>
        ))}
        <DashboardLayout.MenuItem to="/dashboard/prep/support-engineer">
          Prep: Application Support Engineer
        </DashboardLayout.MenuItem>
      </DashboardLayout.MenuGroup>

      {/* Psicología — tests y capturas */}
      <DashboardLayout.MenuGroup label="Psicología" icon={<FlaskIcon />}>
        {PSICOLOGIA_RESOURCES.map((r) => (
          <DashboardLayout.MenuItem key={r.slug} to={`/dashboard/recursos/${r.slug}`}>
            {r.title}
          </DashboardLayout.MenuItem>
        ))}
      </DashboardLayout.MenuGroup>

      {/* Ofertas de trabajo */}
      <DashboardLayout.MenuGroup label="Ofertas de trabajo" icon={<BriefcaseIcon />}>
        {OFERTAS_RESOURCES.map((r) => (
          <DashboardLayout.MenuItem key={r.slug} to={`/dashboard/recursos/${r.slug}`}>
            {r.title}
          </DashboardLayout.MenuItem>
        ))}
      </DashboardLayout.MenuGroup>

      {/* Solo visible para cuentas admin — Grafana queda expuesto con su
          propio login, no forma parte de la sesión de este panel. */}
      {isAdmin && GRAFANA_URL && (
        <DashboardLayout.ExternalMenuItem href={GRAFANA_URL} icon={<BarChartIcon />}>
          Monitoreo (Grafana)
        </DashboardLayout.ExternalMenuItem>
      )}
    </>
  );
}
